/**
 * /tm-tickets/$ticketId — T&M Ticket detail, edit, and billable preview.
 *
 * Sections:
 *  - Header info (read-only with Edit toggle)
 *  - Labor / Materials / Equipment line tables
 *  - Billable Preview panel (rate-sheet picker → live computed amounts)
 *  - Status actions: Mark Eligible / Void
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Chip, Divider, MenuItem, TextField, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { api } from "@/lib/api";
import {
  tmTicketDetailQueryOptions,
  tmTicketBillablePreviewQueryOptions,
  rateSheetsListQueryOptions,
  tmTicketKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type { TMTicket } from "@/lib/types";

export const Route = createFileRoute("/_auth/tm-tickets/$ticketId")({
  component: TMTicketDetailPage,
});

const STATUS_CHIP: Record<string, { label: string; color: "default" | "warning" | "success" | "error" | "info" }> = {
  draft:    { label: "Draft",    color: "default"  },
  eligible: { label: "Eligible", color: "success"  },
  invoiced: { label: "Invoiced", color: "info"     },
  voided:   { label: "Voided",   color: "error"    },
};

function TMTicketDetailPage() {
  const { ticketId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const ticketQ = useQuery(tmTicketDetailQueryOptions(ticketId));
  const ticket = ticketQ.data;

  // Billable preview state
  const [selectedRateSheetId, setSelectedRateSheetId] = useState("");
  const sheetsQ = useQuery(rateSheetsListQueryOptions(
    ticket ? { project_id: ticket.project_id } : {}
  ));
  const previewQ = useQuery(
    tmTicketBillablePreviewQueryOptions(ticketId, selectedRateSheetId)
  );

  // Status transitions
  const markEligible = useMutation({
    mutationFn: () =>
      api<TMTicket>(`/api/money/tm-tickets/${ticketId}`, {
        method: "PATCH",
        body: { status: "eligible" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tmTicketKeys.detail(ticketId) });
      qc.invalidateQueries({ queryKey: tmTicketKeys.all });
      toast.success("Ticket marked eligible");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const voidTicket = useMutation({
    mutationFn: () =>
      api(`/api/money/tm-tickets/${ticketId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tmTicketKeys.all });
      toast.success("Ticket voided");
      navigate({ to: "/tm-tickets" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (ticketQ.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!ticket) return <div className="p-6 text-slate-500">Ticket not found.</div>;

  const chip = STATUS_CHIP[ticket.status] ?? { label: ticket.status, color: "default" };
  const canEdit = ticket.status === "draft";

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate({ to: "/tm-tickets" })}
            size="small"
          >
            All Tickets
          </Button>
          <Typography variant="h5">
            PCO {ticket.pco_number} — {ticket.work_date}
          </Typography>
          <Chip label={chip.label} color={chip.color} size="small" />
        </div>
        <div className="flex gap-2">
          {ticket.status === "draft" && (
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={markEligible.isPending}
              onClick={() => markEligible.mutate()}
            >
              Mark Eligible
            </Button>
          )}
          {(ticket.status === "draft" || ticket.status === "eligible") && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={voidTicket.isPending}
              onClick={() => {
                if (confirm("Void this ticket?")) voidTicket.mutate();
              }}
            >
              Void
            </Button>
          )}
        </div>
      </div>

      {/* Header details */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Header</Typography>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Project" value={ticket.project_id} />
          <Field label="PCO #" value={ticket.pco_number} />
          <Field label="PCO Description" value={ticket.pco_description} />
          <Field label="Work Date" value={ticket.work_date} />
          <Field label="Ticket #" value={ticket.ticket_number} />
          <Field label="Source" value={ticket.source} />
          <Field label="Source Ref" value={ticket.source_ref} />
          <Field label="Notes" value={ticket.notes} />
        </div>
        {canEdit && (
          <div className="mt-4">
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate({ to: "/tm-tickets/$ticketId/edit" as any, params: { ticketId } })}
            >
              Edit Ticket
            </Button>
          </div>
        )}
      </div>

      {/* Labor */}
      {ticket.labor.length > 0 && (
        <SectionCard title="Labor">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Worker</TableCell>
                <TableCell>Craft</TableCell>
                <TableCell align="right">Reg Hrs</TableCell>
                <TableCell align="right">OT Hrs</TableCell>
                <TableCell align="right">DT Hrs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.labor.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.worker_name}</TableCell>
                  <TableCell>{l.craft}</TableCell>
                  <TableCell align="right">{l.regular_hours}</TableCell>
                  <TableCell align="right">{l.ot_hours}</TableCell>
                  <TableCell align="right">{l.dt_hours}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* Materials */}
      {ticket.materials.length > 0 && (
        <SectionCard title="Materials">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Tax</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.materials.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.description}</TableCell>
                  <TableCell>{m.vendor ?? "—"}</TableCell>
                  <TableCell>{m.category ?? "—"}</TableCell>
                  <TableCell align="right">${Number(m.amount).toFixed(2)}</TableCell>
                  <TableCell align="right">${Number(m.tax).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* Equipment */}
      {ticket.equipment.length > 0 && (
        <SectionCard title="Equipment">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Equipment Class</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell>Rate Basis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.equipment.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.equipment_class}</TableCell>
                  <TableCell align="right">{e.units}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{e.rate_basis}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* Subcontractors */}
      {ticket.subcontractors.length > 0 && (
        <SectionCard title="Subcontractor Passthrough">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Sub Ticket Ref</TableCell>
                <TableCell>Cost Code</TableCell>
                <TableCell align="right">Raw Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.subcontractors.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.description}</TableCell>
                  <TableCell>{s.sub_ticket_ref ?? "—"}</TableCell>
                  <TableCell>{s.cost_code ?? "—"}</TableCell>
                  <TableCell align="right">${Number(s.raw_cost).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* ── Billable Preview (M4) ── */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Billable Preview</Typography>
        <TextField
          select
          label="Rate Sheet"
          value={selectedRateSheetId}
          onChange={(e) => setSelectedRateSheetId(e.target.value)}
          size="small"
          sx={{ width: 300, mb: 2 }}
        >
          <MenuItem value="" disabled>Select a rate sheet</MenuItem>
          {(sheetsQ.data?.items ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} ({s.effective_from} – {s.effective_to ?? "open"})
            </MenuItem>
          ))}
        </TextField>

        {previewQ.isLoading && selectedRateSheetId && (
          <div className="text-sm text-slate-400">Computing…</div>
        )}
        {previewQ.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {(previewQ.error as Error).message}
          </Alert>
        )}
        {previewQ.data && (
          <div className="space-y-3">
            <div className="grid grid-cols-5 gap-4">
              <KPI label="Labor" value={`$${Number(previewQ.data.labor_total).toFixed(2)}`} />
              <KPI label="Materials" value={`$${Number(previewQ.data.materials_total).toFixed(2)}`} />
              <KPI label="Equipment" value={`$${Number(previewQ.data.equipment_total).toFixed(2)}`} />
              <KPI label="Subs" value={`$${Number(previewQ.data.subs_total).toFixed(2)}`} />
              <KPI label="Grand Total" value={`$${Number(previewQ.data.grand_total).toFixed(2)}`} bold />
            </div>

            {previewQ.data.labor.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Worker / Craft</TableCell>
                    <TableCell align="right">Reg</TableCell>
                    <TableCell align="right">OT</TableCell>
                    <TableCell align="right">DT</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewQ.data.labor.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.worker_name} / {l.craft}</TableCell>
                      <TableCell align="right">${Number(l.regular_billable).toFixed(2)}</TableCell>
                      <TableCell align="right">${Number(l.ot_billable).toFixed(2)}</TableCell>
                      <TableCell align="right">${Number(l.dt_billable).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${Number(l.total_billable).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {previewQ.data.subs.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sub — Description</TableCell>
                    <TableCell align="right">Raw Cost</TableCell>
                    <TableCell align="right">Markup %</TableCell>
                    <TableCell align="right">Billable</TableCell>
                    <TableCell align="right">Margin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewQ.data.subs.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{s.description}</TableCell>
                      <TableCell align="right">${Number(s.raw_cost).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(s.markup_percent).toFixed(1)}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${Number(s.billable).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "success.main" }}>
                        ${Number(s.margin).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className="text-slate-500">{label}: </span>
      <span className="font-medium">{value ?? "—"}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 dark:bg-gray-900 dark:border-gray-700">
      <div className="px-5 py-3 border-b border-slate-100 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

function KPI({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded p-3">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-lg ${bold ? "font-bold text-emerald-600" : "font-semibold"}`}>
        {value}
      </div>
    </div>
  );
}
